import { Button, Input } from "@/shared";
import Header from "@/shared/components/ui/Header";
import MainLayout from "@/shared/components/ui/MainLayout";
import {
  Document,
  Font,
  Page,
  pdf,
  Text,
  View,
  Image,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import bwipjs from 'bwip-js';

Font.register({
  family: "Tajawal",
  src: "https://fonts.gstatic.com/s/tajawal/v10/Iura6YBj_oCad4k1rzaLCr5IlLA.ttf",
});


const PrintLabels = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name');
  const barcode = searchParams.get('barcode') || '';
  const isSerialLabels = searchParams.get('serial_labels') === 'serial_labels';

  console.log(isSerialLabels);


  const storedItems = localStorage.getItem('items');
  const items = storedItems ? JSON.parse(localStorage.getItem('items') || '') : [];
  const storedSerialLabels = localStorage.getItem('serial_labels');
  const serialLabels = storedSerialLabels ? JSON.parse(localStorage.getItem('serial_labels') || '') : [];


  const [dimensions, setDimensions] = useState(() => {
    const saved = localStorage.getItem('label_settings')
    return saved ?
      JSON.parse(saved) :
      {
        width: 40,
        height: 25,
        number: 1,
        displayName: true,
        fontSize: 10,
        codeSize: 15,
        displayQty: true,
        factor: 2.83
      }
  });


  useEffect(() => {
    localStorage.setItem('label_settings', JSON.stringify(dimensions))
  }, [dimensions]);

  const handleChange = (e: any) => {
    const { id, value } = e.target;
    setDimensions((prev: any) => ({ ...prev, [id]: value }))
  }


  async function generateBarcodeDataURL(value: string, format: string = 'code128') {
    try {
      const canvas = document.createElement('canvas');
      const factor = Number(dimensions.factor) || 1;
      bwipjs.toCanvas(canvas, {
        bcid: format,
        text: value,
        scale: 3 * factor,
        height: Number(dimensions.codeSize) * factor,
        includetext: false,
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.log(error);
      return null
    }
  }


  const exportPdf = async () => {
    const factor = Number(dimensions.factor) || 1;
    try {
      let rawItems: { name: string, barcodeValue: string }[] = [];

      if (isSerialLabels) {
        serialLabels.forEach((item: any) => {
          const count = Number(item?.qty) || 1;
          for (let i = 0; i < count; i++) {
            rawItems.push({
              name: item?.name,
              barcodeValue: item?.barcode
            });
          }
        });
      }
      else if (name) {
        rawItems = Array.from({ length: Number(dimensions.number) }).map(() =>
        ({
          name: name,
          barcodeValue: barcode
        }));
      }
      else {
        items.forEach((item: any) => {
          const count = Number(item?.qty) || 1
          for (let i = 0; i < count; i++) {
            rawItems.push({ name: item?.name, barcodeValue: barcode || item?.barcode })
          }
        });
      }

      const printData = await Promise.all(
        rawItems.map(async (item) => {
          const barcodeDataUrl = await generateBarcodeDataURL(item?.barcodeValue || '12345');
          return { ...item, barcodeImage: barcodeDataUrl }
        })
      )
      const blob = await pdf(
        <Document>
          {
            printData.map((item: any, index) => {
              const pageWidth = Number(dimensions.width) * factor;
              const pageHeight = Number(dimensions.height) * factor;
              const fontSize = Number(dimensions.fontSize) * factor;
              const barcodeHeight = Number(dimensions.codeSize) * factor;
              return (
                <Page key={index}
                  size={{
                    width: `${pageWidth}mm`,
                    height: `${pageHeight}mm`
                  }}
                  style={{ padding: 5, fontFamily: "Tajawal", }}>
                  <View style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '0.5pt solid #eee'
                  }}>
                    {!isSerialLabels && (
                      <Text style={{
                        fontSize: fontSize * 0.9,
                        marginTop: 1,
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '90%',
                        letterSpacing: 2,
                        textTransform: 'uppercase'
                      }}>
                        {item?.name}
                      </Text>
                    )}

                    {item?.barcodeImage && (
                      <Image
                        src={item?.barcodeImage}
                        style={{
                          width: '90%',
                          height: barcodeHeight,
                        }}
                      />
                    )}
                    <Text style={{
                      fontSize: fontSize * 0.9,
                      marginTop: 2,
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: '90%',
                      letterSpacing: 2,
                      textTransform: 'uppercase'
                    }}>
                      {item?.barcodeValue}
                    </Text>
                  </View>
                </Page>
              )
            })
          }
        </Document>
      ).toBlob();
      saveAs(blob, isSerialLabels ? "serial_labels" : (name ?? "labels"));
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <MainLayout>
      <Header title={t("Sticker Settings")} info="" />
      <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center">
        <div className="max-w-4xl mx-auto">

          <div className="p-6 bg-white rounded-[1rem] shadow-xl shadow-slate-200/50">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">
              {t("Sticker Settings")}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500">{t("Width (mm)")}</label>
                <Input id="width" type="number" value={dimensions.width} onChange={handleChange} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500">{t("Height (mm)")}</label>
                <Input id="height" type="number" value={dimensions.height} onChange={handleChange} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500">{t("Font Size")}</label>
                <Input id="fontSize" type="number" value={dimensions.fontSize} onChange={handleChange} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500">{t("Factor")}</label>
                <Input id="factor" type="number" value={dimensions.factor} onChange={handleChange} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500">{t("Barcode Height")}</label>
                <Input id="codeSize" type="number" value={dimensions.codeSize} onChange={handleChange} />
              </div>
              {name && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500">{t("Number of Labels")}</label>
                  <Input id="number" type="number" value={dimensions.number} onChange={handleChange} />
                </div>
              )}

            </div>
            <Button
              onClick={exportPdf}
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-all"
            >
              {t("Print Labels")}
            </Button>
          </div>
        </div>
      </main>
    </MainLayout>

  );
}

export default PrintLabels;